// MFA challenge — shown after password login when the user has a verified TOTP
// factor but the session is still AAL1.
//
// Refer: https://supabase.com/docs/guides/auth/auth-mfa#step-by-step-example
//
// Flow:
//   1. supabase.auth.mfa.listFactors() → pick the verified TOTP factor.
//   2. supabase.auth.mfa.challenge({ factorId }) → returns a challengeId.
//   3. User types the 6-digit TOTP code from their authenticator app.
//   4. supabase.auth.mfa.verify({ factorId, challengeId, code }) → on success
//      the session is upgraded to AAL2 and we redirect to /dashboard.
//
// We combine challenge + verify into challengeAndVerify() for simplicity.
import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Shield } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";

export function MfaChallengePage() {
  const navigate = useNavigate();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootstrap, setBootstrap] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      // Pick the first verified TOTP factor. If there isn't one, the user
      // shouldn't have hit this page — bounce them home.
      const { data, error: listErr } = await supabase.auth.mfa.listFactors();
      if (!active) return;
      if (listErr) {
        setError(listErr.message);
        setBootstrap(false);
        return;
      }
      const verified = (data?.totp ?? []).find((f) => f.status === "verified");
      if (!verified) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setFactorId(verified.id);
      setBootstrap(false);
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code");
      return;
    }

    setVerifying(true);
    setError(null);
    const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: code.trim(),
    });
    setVerifying(false);

    if (verifyErr) {
      setError(verifyErr.message);
      return;
    }

    navigate("/dashboard", { replace: true });
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-factor authentication
          </CardTitle>
          <CardDescription>
            Enter the 6-digit code from your authenticator app to finish
            signing in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bootstrap ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="totp-code">Authentication code</Label>
                <Input
                  id="totp-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="123456"
                  required
                />
              </div>

              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={verifying || code.length !== 6}
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying
                  </>
                ) : (
                  "Verify"
                )}
              </Button>

              <button
                type="button"
                onClick={handleSignOut}
                className="block w-full text-center text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
              >
                Sign out and try again
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
